import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { setDrawModule, setDrawFeatures, setDrawnFeatures, setDrawingFeature, setDrawnFeatureCoordinates, setDrawingModuleMode, setDrawingModuleIdle } from '../../redux/map/Actions';

import Draw from '../../Components/Mapbox/Draw/Draw';

function withDrawControl (WrappedComponent) {
    class WithDrawControl extends React.Component {
        static propTypes = {
            id: PropTypes.string.isRequired,
            drawControls: PropTypes.object.isRequired,
            drawnFeatures: PropTypes.any,
            // callbacks
            onMapLoaded: PropTypes.func,
            onDrawLoaded: PropTypes.func,
            onFeatureChange: PropTypes.func,
            // Redux state

            // Redux dispatch
            dispatchSetDrawFeatures: PropTypes.func,
            dispatchDrawModeChange: PropTypes.func, 
            dispatchDrawModuleIdle: PropTypes.func
        };

        static defaultProps = {

        };

        constructor (props) {
            super(props);

            this.draw = null;
            this.drawControl = null;

            this.handleMapLoaded = this.handleMapLoaded.bind(this);
            this.setDrawnFeatures = this.setDrawnFeatures.bind(this);
            this.handleFeatureChange = this.handleFeatureChange.bind(this);

            this.handleDrawCreate = this.handleDrawCreate.bind(this);
            this.handleDrawDelete = this.handleDrawDelete.bind(this);
            this.handleDrawSelectionChange = this.handleDrawSelectionChange.bind(this);
            this.handleDrawUpdate = this.handleDrawUpdate.bind(this);
            this.handleDrawEditing = this.handleDrawEditing.bind(this);
            this.onDrawModeChange = this.onDrawModeChange.bind(this);
        }

        componentWillUnmount () {
            const { setDrawModule } = this.props;
            setDrawModule(null);
        }

        handleMapLoaded (map) {
            const { drawControls, drawEvents, drawnFeatures, drawingFeature, onMapLoaded, onDrawLoaded, drawStyles, setDrawModule } = this.props;
            this.draw = new Draw({
                map,
                drawControls: drawControls,
                updateDrawnFeature: this.handleFeatureChange,
                drawStyles: drawStyles,
                onDrawModeChange: this.onDrawModeChange
            });
            this.drawControl = this.draw.getDrawControl();

            map.addControl(this.drawControl, 'top-right');

            if (drawEvents) {
                const eventKeys = Object.keys(drawEvents);
                for (const eventKey of eventKeys) {
                    map.on(eventKey, drawEvents[eventKey]);
                }
            }

            // TODO: Previously could set drawn features but right now this isnt used
            // Possible new parameter passed as prop for setting so no conflict with redux name
            // if (drawnFeatures)
            //     this.setDrawnFeatures(drawnFeatures)

            map.on('draw.create', this.handleDrawCreate);
            map.on('draw.delete', this.handleDrawDelete);
            map.on('draw.selectionchange', this.handleDrawSelectionChange);
            map.on('draw.update', this.handleDrawUpdate);
            map.on('draw.editing', this.handleDrawEditing);

            onMapLoaded && onMapLoaded(map);
            onDrawLoaded && onDrawLoaded(this.draw);

            // If there is an existing state then load that in
            if (drawnFeatures) { this.drawControl.set(drawnFeatures); }
            if (drawingFeature) { this.drawControl.changeMode('direct_select', { featureId: drawingFeature.id }); }

            // The redux state set needs to go last otherwise state updates will trigger before the features are in the draw control
            // Can cause issues since code assumes certain things will be in the draw control based off the redux state
            setDrawModule(this.draw);
        }


        onDrawModeChange (mode){
            const {dispatchDrawModeChange, dispatchDrawModuleIdle} = this.props;
            if(mode === "simple_select" || mode === "direct_select"){
                setTimeout(()=>dispatchDrawModuleIdle(),10);
            } else {
                dispatchDrawModeChange(mode);
            }
        }


        handleDrawCreate (e) {
            const { drawModule, dispatchSetDrawingFeature, dispatchSetDrawnFeatures } = this.props;
            // console.log('create', e)
            dispatchSetDrawnFeatures({
                type: 'FeatureCollection',
                features: _.cloneDeep(drawModule.getDrawControl().getAll().features)
            });
        }

        handleDrawDelete (e) {
            const { drawModule, dispatchSetDrawFeatures } = this.props;
            // console.log('delete', e)
            // console.log(_.cloneDeep(drawModule.getDrawControl().getAll().features))
            dispatchSetDrawFeatures({
                type: 'FeatureCollection',
                features: _.cloneDeep(drawModule.getDrawControl().getAll().features)
            }, null);
        }

        handleDrawSelectionChange (e) {
            const { drawModule, dispatchSetDrawFeatures } = this.props;
            // console.log('selectionchange', e)
            // Selection change can be triggered from a delete so need to refresh drawn features as well as selected
            dispatchSetDrawFeatures(
                {
                    type: 'FeatureCollection',
                    features: _.cloneDeep(drawModule.getDrawControl().getAll().features)
                },
                e.features.length > 0 ? _.cloneDeep(e.features[0]) : null
            );
        }

        handleDrawUpdate (e) {
            const { dispatchSetDrawingFeature, dispatchSetDrawnFeatureCoordinates } = this.props;
            // console.log('updating', e)
            dispatchSetDrawingFeature((e.features.length > 0 && e.features[0].geometry.coordinates.length > 0) ? _.cloneDeep(e.features[0]) : null);
            if (e.features[0].geometry.coordinates.length !== 0) { dispatchSetDrawnFeatureCoordinates(e.features[0].id, e.features[0].geometry.coordinates); }
        }

        handleDrawEditing (e) {
            const { drawModule, drawingFeature, dispatchSetDrawingFeature } = this.props;
            // console.log('editing', e);
            if (drawModule.getDrawControl().getMode() === 'extrude_mode') {
                dispatchSetDrawingFeature(drawModule.getDrawControl().getSelected().features[0]);
            } else {
                dispatchSetDrawingFeature(e.features[0]);
            }
        }

        setDrawnFeatures (drawnFeatures) {
            if (this.drawControl) {
                this.drawControl.set(drawnFeatures);
                const selectedIds = this.drawControl.getAll().features
                    .filter(feature => feature.properties.active === 'true')
                    .map(feature => feature.id);
                this.drawControl.changeMode('simple_select', { featureIds: selectedIds });
            }
        }

        handleFeatureChange = feature => {
            const { onFeatureChange } = this.props;
            onFeatureChange && onFeatureChange(feature);
        };

        render () {
            const { onFeatureChange, onDrawModeChange, drawControls, drawnFeatures, ...props } = this.props;
            return (
                <WrappedComponent
                    {...this.props}
                    onMapLoaded={this.handleMapLoaded}
                />
            );
        }
    }

    const wrappedComponentName = WrappedComponent.displayName
        || WrappedComponent.name
        || 'Component';

    WithDrawControl.displayName = `withDrawControl(${wrappedComponentName})`;
    return WithDrawControl;
}


const mapStateToProps = (state, ownProps) => {
    return {
        map: state[ownProps.id],
        drawModule: state[ownProps.id].drawModule,
        drawnFeatures: state[ownProps.id].drawnFeatures,
        drawingFeature: state[ownProps.id].drawingFeature
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    setDrawModule: (drawModule) => dispatch(setDrawModule(drawModule, ownProps.id)),
    dispatchSetDrawFeatures: (drawnFeatures, selectedFeature) => dispatch(setDrawFeatures(ownProps.id, drawnFeatures, selectedFeature)),
    dispatchSetDrawnFeatures: (val) => dispatch(setDrawnFeatures(val, ownProps.id)),
    dispatchSetDrawingFeature: (val) => dispatch(setDrawingFeature(val, ownProps.id)),
    dispatchSetDrawnFeatureCoordinates: (id, coordinates) => dispatch(setDrawnFeatureCoordinates(id, coordinates, ownProps.id)),
    dispatchDrawModeChange: (mode) => dispatch(setDrawingModuleMode(mode, ownProps.id)),
    dispatchDrawModuleIdle: () => dispatch(setDrawingModuleIdle(ownProps.id))
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withDrawControl);